import React from "react";
import { motion } from "framer-motion";
import { Glasses, PlusCircle } from "lucide-react";
import { ArrowDown } from "lucide-react";
import SectionHeader from "./SectionHeader";
import BrandStripe from "./BrandStripe";

/**
 * "The Good Eyes Shopping Journey" — vertical layout matching brand book.
 * STEP label + description on the LEFT, illustration circle on the RIGHT.
 * Down arrows sit between rows.
 */

const ColourWheel = ({ size = 96 }) => {
  const colors = ["#F2B41C", "#111", "#001F5B", "#005B3D", "#C7332A", "#8A5A2C", "#D6D3CC"];
  const r = size / 2;
  const cx = r, cy = r;
  const inner = r * 0.5;
  const paths = colors.map((c, i) => {
    const a1 = (i / colors.length) * Math.PI * 2 - Math.PI / 2;
    const a2 = ((i + 1) / colors.length) * Math.PI * 2 - Math.PI / 2;
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const x3 = cx + inner * Math.cos(a2), y3 = cy + inner * Math.sin(a2);
    const x4 = cx + inner * Math.cos(a1), y4 = cy + inner * Math.sin(a1);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 0 0 ${x4} ${y4} Z`;
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
      {paths.map((d, i) => (
        <path key={i} d={d} fill={colors[i]} />
      ))}
      <circle cx={cx} cy={cy} r={inner} fill="none" stroke="rgba(0,31,91,0.15)" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(0,31,91,0.15)" strokeWidth="1" />
    </svg>
  );
};

const steps = [
  {
    label: "Step 1",
    title: "Choose your style.",
    desc: "Eight styles designed for everyday wear.",
    bg: "var(--ge-beige)",
    render: () => <Glasses size={54} strokeWidth={1.5} className="text-[color:var(--ge-navy)]" />,
  },
  {
    label: "Step 2",
    title: "Choose your colour.",
    desc: "Nine premium finishes, soft-touch or polished.",
    bg: "#F4EBD9",
    render: () => <ColourWheel size={110} />,
  },
  {
    label: "Step 3",
    title: "Find your strength.",
    desc: "Five reading strengths from +1.0 to +3.0.",
    bg: "var(--ge-red)",
    render: () => (
      <span className="text-white font-bold text-4xl tight-cap">+2.0</span>
    ),
  },
];

export const ShoppingJourney = () => {
  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="relative bg-white py-24 md:py-32 border-t border-[color:var(--ge-navy)]/10 overflow-hidden"
    >
      <div className="ge-container">
        <SectionHeader
          kicker="The Good Eyes Shopping Journey"
          title={<>Three steps to<br /><span style={{ color: "var(--ge-yellow)" }}>your pair</span>.</>}
          subtitle="No complicated charts. No guesswork. Just Good Eyes, a shopping experience designed to make picking readers feel easy."
        />

        <div className="mt-14 md:mt-20 flex flex-col gap-2 md:gap-4">
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className="grid grid-cols-[1fr_auto] items-center gap-8 md:gap-16 py-6"
                data-testid={`journey-step-${i + 1}`}
              >
                <div className="max-w-md">
                  <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--ge-navy)]/60">
                    {s.label}
                  </div>
                  <h3 className="mt-2 tight-cap font-bold text-[color:var(--ge-navy)] text-3xl md:text-5xl leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-base md:text-lg font-light text-[color:var(--ge-navy)]/75">
                    {s.desc}
                  </p>
                </div>
                <div
                  className="shrink-0 rounded-full flex items-center justify-center"
                  style={{
                    background: s.bg,
                    width: "clamp(120px, 18vw, 200px)",
                    height: "clamp(120px, 18vw, 200px)",
                  }}
                >
                  {s.render()}
                </div>
              </motion.div>
              {i < steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 + 0.2 }}
                  className="ml-0 md:ml-6 pl-0 md:pl-2 self-start"
                >
                  <span className="inline-flex items-center justify-center text-[color:var(--ge-navy)]/60">
                    <ArrowDown size={28} strokeWidth={1.5} />
                  </span>
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-16 md:mt-20 border-t border-[color:var(--ge-navy)]/15 pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-lg md:text-xl font-semibold text-[color:var(--ge-navy)]">
            No complicated charts. No guesswork.{" "}
            <span style={{ color: "var(--ge-red)" }}>Just Good Eyes.</span>
          </div>
          <a
            href="/shop"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--ge-navy)] text-white px-6 py-3 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[color:var(--ge-red)] transition-colors self-start"
          >
            Start with a style
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <BrandStripe height={22} />
      </div>
    </section>
  );
};

export default ShoppingJourney;
